import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import AuthContext from '../../Authentication/components/AuthContext';
import useActivations from '../../Activations/components/hooks/useActivations';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { Segmented, TCard, TEmpty, TRow, TSkeleton } from '../components/table';
import { CountUp, Sparkline } from '../components/live';
import '../home-expanded.css';
import TwoWeekCalendar, { CalEvent, CalLegend, addDays, dayStart, endDayOf, mondayOf, shortTime } from '../components/calendar';
import '../calendar.css';
import { PageHeadActions } from '../components/nav';
import { readinessOf, useEventDetails } from '../lib/schedule';
import { SANDBOX_DATA, sandboxGet } from '../lib/sandbox';
import { LIVE_WINDOW_MS } from '../chrome/useEventChrome';
import { DAY, dateRangeLabel, dayOf, startOfToday } from '../lib/jobs';

/*
 * Home: Live now, schedule, Up next, Latest captures, My tasks.
 * Tasks, weather, capture setup and the sparkline use fake-data-only
 * endpoints (no backend yet) and are skipped against a real server.
 */

interface Act {
    id: number;
    name: string;
    date?: string;
    location?: string;
    isArchived?: boolean;
}

function ago(iso?: string) {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 90 * 1000) return 'just now';
    if (ms < 90 * 60 * 1000) return `${Math.round(ms / 60000)}m ago`;
    if (ms < 36 * 3600 * 1000) return `${Math.round(ms / 3600000)}h ago`;
    return `${Math.round(ms / 86400000)}d ago`;
}

function dueLabel(iso: string) {
    const d = dayOf(iso);
    if (d === null) return '';
    const diff = Math.round((d - startOfToday()) / DAY);
    const time = new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    if (diff < 0) return 'Overdue';
    if (diff === 0) return `Today ${time}`;
    if (diff === 1) return 'Tomorrow';
    return dateRangeLabel(iso);
}

export default function HomeV3() {
    const navigate = useNavigate();
    const { auth } = useContext(AuthContext);
    const { getActivations } = useActivations();
    const api = useAuthenticatedApi();

    const [acts, setActs] = useState<Act[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [stats, setStats] = useState<Record<number, any>>({});
    const [extra, setExtra] = useState<Record<number, { devices?: any[]; setup?: any; weather?: any }>>({});
    const [photos, setPhotos] = useState<any[] | null>(null);
    const [shares, setShares] = useState<Record<number, number>>({});
    const [hourly, setHourly] = useState<Record<number, number[]>>({});
    /* Live now layout, remembered per browser. */
    const [view, setView] = useState<'compact' | 'expanded'>(() => {
        try {
            return window.localStorage.getItem('ps.home.live') === 'expanded' ? 'expanded' : 'compact';
        } catch {
            return 'compact';
        }
    });
    const chooseView = (v: 'compact' | 'expanded') => {
        setView(v);
        try {
            window.localStorage.setItem('ps.home.live', v);
        } catch {
            /* a convenience only */
        }
    };
    const [tasks, setTasks] = useState<any[] | null>(null);

    const load = useCallback(async () => {
        try {
            const list: Act[] = (await getActivations()) ?? [];
            setActs(list);
            setFailed(false);
            const today = startOfToday();
            const soon = list.filter((a) => {
                const d = dayOf(a.date?.split('T')[0] + 'T12:00:00');
                return !a.isArchived && d !== null && d >= today - DAY && d < today + 7 * DAY;
            });
            const pairs = await Promise.all(
                soon.map(async (a) => {
                    const [st, dv, setup, wx] = await Promise.all([
                        api.get(`/activations/${a.id}/photoStats`).then((r: any) => r.data).catch(() => null),
                        api.get(`/activations/${a.id}/devices`).then((r: any) => r.data).catch(() => undefined),
                        sandboxGet(api, `/dev/capture-setup/${a.id}`),
                        sandboxGet(api, `/dev/weather/${a.id}`),
                    ]);
                    return [a.id, st, { devices: dv, setup, weather: wx }] as const;
                }),
            );
            setStats(Object.fromEntries(pairs.map(([id, st]) => [id, st])));
            setExtra(Object.fromEntries(pairs.map(([id, , x]) => [id, x])));
            const liveIds = pairs
                .filter(([, st]) => st?.lastPhoto?.createdAt && Date.now() - new Date(st.lastPhoto.createdAt).getTime() < LIVE_WINDOW_MS)
                .map(([id]) => id);
            /* Same share totals as the event Overview. */
            const shareTotals = await Promise.all(
                liveIds.map((id) =>
                    api
                        .get(`/activations/${id}/shareStats`)
                        .then((r: any) => [id, (r.data ?? []).reduce((n: number, d: any) => n + Number(d.total ?? 0), 0)] as const)
                        .catch(() => [id, NaN] as const),
                ),
            );
            setShares(Object.fromEntries(shareTotals));
            /* Sparkline data (fake-data-only endpoint). */
            const series = await Promise.all(
                liveIds.map((id) =>
                    sandboxGet(api, `/dev/hourly/${id}`).then(
                        (data: any) => [id, (data ?? []).map((b: any) => Number(b.n ?? 0))] as const,
                    ),
                ),
            );
            setHourly(Object.fromEntries(series));
            const shots = (
                await Promise.all(
                    liveIds.map((id) => api.get(`/activations/${id}/photos`).then((r: any) => r.data ?? []).catch(() => [])),
                )
            ).flat();
            setPhotos(shots.sort((a: any, b: any) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')));
        } catch {
            /* A failed poll keeps the last data; only a failed first load shows an error. */
            setActs((cur) => {
                if (cur === null) setFailed(true);
                return cur;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        /* Tasks have no backend yet. */
        if (SANDBOX_DATA)
            api.get('/tasks')
                .then((r: any) => setTasks(r.data ?? []))
                .catch(() => setTasks([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /* Poll every 30s, or every 10s in Expanded. */
    useEffect(() => {
        const t = window.setInterval(load, view === 'expanded' ? 10000 : 30000);
        return () => window.clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    const isLive = (id: number) => {
        const iso = stats[id]?.lastPhoto?.createdAt;
        return !!iso && Date.now() - new Date(iso).getTime() < LIVE_WINDOW_MS;
    };
    const byId = useMemo(() => new Map((acts ?? []).map((a) => [a.id, a])), [acts]);
    const live = (acts ?? []).filter((a) => isLive(a.id));
    const upNext = (acts ?? [])
        .filter((a) => {
            const d = dayOf(a.date?.split('T')[0] + 'T12:00:00');
            const today = startOfToday();
            return !a.isArchived && !isLive(a.id) && d !== null && d >= today && d < today + 7 * DAY;
        })
        .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));

    const hour = new Date().getHours();
    const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    const name = (auth?.username ?? '').split(/[\s.@]/)[0];
    const who = name ? `, ${name[0].toUpperCase()}${name.slice(1)}` : '';
    const todayLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

    /* Two-week schedule. Fetches per event; an org-wide endpoint would replace this. */
    const [calStart, setCalStart] = useState(() => mondayOf(Date.now()));
    const actDay = (a: Act) => (a.date ? dayStart(new Date(`${a.date.split('T')[0]}T12:00:00`)) : null);
    const calIds = useMemo(() => {
        const from = addDays(calStart, -14);
        const to = addDays(calStart, 13);
        return (acts ?? [])
            .filter((a) => {
                const d = actDay(a);
                return !a.isArchived && d !== null && d >= from && d <= to;
            })
            .map((a) => a.id)
            .sort((x, y) => x - y);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acts, calStart]);
    /* fetched once per event, kept across the live-data polls */
    const calInfo = useEventDetails(calIds, ['prod', 'devices', 'setup']);
    const calEvents: CalEvent[] = useMemo(() => {
        return (acts ?? [])
            .filter((a) => !a.isArchived)
            .map((a) => {
                const info = calInfo[a.id] ?? {};
                const startDay = info.prod?.startAt ? dayStart(info.prod.startAt) : actDay(a);
                if (startDay === null) return null;
                const endDay = info.prod?.endAt ? endDayOf(info.prod.endAt, startDay) : startDay;
                const { status, note } = readinessOf(
                    info,
                    endDay,
                    isLive(a.id) && `Live · ${ago(stats[a.id]?.lastPhoto?.createdAt)}`,
                );
                return {
                    id: a.id,
                    name: a.name,
                    startDay,
                    endDay,
                    time: shortTime(info.prod?.startAt),
                    status,
                    note,
                    location: a.location,
                    href: `/activations/${a.id}`,
                } as CalEvent;
            })
            .filter(Boolean) as CalEvent[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acts, calInfo, stats]);

    const readiness = (a: Act) => {
        const x = extra[a.id];
        if (!x || x.devices === undefined) return <span className="rd">Checking…</span>;
        const n = x.devices.length;
        if (n === 0) return <span className="rd warn">No devices assigned</span>;
        if (x.setup?.app === 'clickerbooth' && !x.setup.experiencePublished)
            return <span className="rd warn">Experience not published</span>;
        return <span className="rd ok">Ready · {n} {n === 1 ? 'device' : 'devices'}</span>;
    };

    const openTasks = (tasks ?? []).filter((t) => t.status !== 'done');
    const setTaskDone = (t: any, done: boolean) => {
        setTasks((list) => (list ?? []).map((x) => (x.id === t.id ? { ...x, status: done ? 'done' : 'open' } : x)));
        api.put(`/tasks/${t.id}`, JSON.stringify({ status: done ? 'done' : 'open' })).catch(() => {
            setTasks((list) => (list ?? []).map((x) => (x.id === t.id ? t : x)));
        });
    };
    const [justDone, setJustDone] = useState<number[]>([]);

    return (
        <AppShell>
            <PageHeadActions
                title={`${greet}${who}`}
                sub={
                    acts
                        ? `${todayLabel} · ${live.length} live now · ${upNext.length} in the next 7 days`
                        : todayLabel
                }
                actions={
                    <button className="tb-new" onClick={() => navigate('/activations/create')}>
                        New activation
                    </button>
                }
            />

            {failed && (
                <TCard>
                    <TEmpty message="Couldn’t load today’s events." actionLabel="Retry" onAction={load} />
                </TCard>
            )}

            {/* Live now */}
            <div className="live-head">
                <p className="s-lab">Live now{acts ? ` · ${live.length}` : ''}</p>
                {live.length > 0 && (
                    <Segmented
                        label="Live now view"
                        options={[
                            { value: 'compact', label: 'Compact' },
                            { value: 'expanded', label: 'Expanded' },
                        ]}
                        value={view}
                        onChange={chooseView}
                    />
                )}
            </div>
            {acts === null && !failed ? (
                <div className="lgrid">
                    <div className="lcard"><TSkeleton rows={2} /></div>
                </div>
            ) : live.length === 0 ? (
                <p className="h-quiet">Nothing is capturing right now.</p>
            ) : (
                <div className={view === 'expanded' ? 'lgrid xl' : 'lgrid'}>
                    {live.map((a) => {
                        const st = stats[a.id] ?? {};
                        const x = extra[a.id] ?? {};
                        const thumbs = (photos ?? []).filter((p) => p.activationId === a.id).slice(0, 4);
                        const activeDevices = (x.devices ?? []).filter(
                            (d: any) => d.lastUploadAt && Date.now() - new Date(d.lastUploadAt).getTime() < LIVE_WINDOW_MS,
                        ).length;
                        if (view === 'expanded') {
                            const mine = (photos ?? []).filter((p) => p.activationId === a.id);
                            const hero = mine[0];
                            const go = (e: React.MouseEvent) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${a.id}`); } };
                            return (
                                <article className="lcard xl" key={a.id}>
                                    <a className="lc-hero" href={`/activations/${a.id}`} onClick={go}>
                                        {hero && <img key={hero.id} src={hero.originalUri ?? hero.thumbnailUri} alt={`Latest capture from ${a.name}`} />}
                                        <span className="lc-over">
                                            <span className="lc-live-chip"><span className="dot pulse" /> Live · {ago(st.lastPhoto?.createdAt)}</span>
                                            <span className="lc-title">{a.name}</span>
                                            <span className="lc-where">
                                                {a.location}
                                                {x.weather ? ` · ${x.weather.temp}° ${x.weather.label}` : ''}
                                            </span>
                                        </span>
                                    </a>
                                    <div className="lc-body">
                                        <div className="lc-stats">
                                            <div><b><CountUp value={Number(st.photoCount ?? 0)} /></b><span>Captures</span></div>
                                            <div><b>{Number.isFinite(shares[a.id]) ? <CountUp value={shares[a.id]} /> : '—'}</b><span>Shares</span></div>
                                            <div><b>{activeDevices}</b><span>{activeDevices === 1 ? 'Device active' : 'Devices active'}</span></div>
                                        </div>
                                        {(hourly[a.id] ?? []).length > 1 && (
                                            <div className="lc-spark">
                                                <Sparkline points={hourly[a.id]} label={`Captures per half hour at ${a.name}`} />
                                                <span>last 6 hours</span>
                                            </div>
                                        )}
                                        {mine.length > 1 && (
                                            <div className="lc-thumbs">
                                                {mine.slice(1, 5).map((p) => (
                                                    <img key={p.id} src={p.thumbnailUri} alt="" loading="lazy" />
                                                ))}
                                            </div>
                                        )}
                                        <div className="lc-actions">
                                            <button className="tb-btn" onClick={() => navigate(`/activations/${a.id}`)}>Open event</button>
                                            {st.slug && (
                                                <a className="tb-btn" href={`/gallery/${st.slug}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                                    Public gallery
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        }
                        return (
                            <article className="lcard" key={a.id}>
                                <a className="lc-head" href={`/activations/${a.id}`} onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${a.id}`); } }}>
                                    <span className="lc-name">{a.name}</span>
                                    <span className="lc-live">
                                        <span className="dot pulse" /> Live · {ago(st.lastPhoto?.createdAt)}
                                    </span>
                                    <span className="lc-sub">
                                        {a.location}
                                        {x.weather ? ` · ${x.weather.temp}° ${x.weather.label}` : ''}
                                    </span>
                                </a>
                                <div className="lc-stats">
                                    <div><b><CountUp value={Number(st.photoCount ?? 0)} /></b><span>Captures</span></div>
                                    <div><b>{Number.isFinite(shares[a.id]) ? <CountUp value={shares[a.id]} /> : '—'}</b><span>Shares</span></div>
                                    <div><b>{activeDevices}</b><span>{activeDevices === 1 ? 'Device active' : 'Devices active'}</span></div>
                                </div>
                                {thumbs.length > 0 && (
                                    <div className="lc-thumbs">
                                        {thumbs.map((p) => (
                                            <img key={p.id} src={p.thumbnailUri} alt="" loading="lazy" />
                                        ))}
                                    </div>
                                )}
                                <div className="lc-actions">
                                    <button className="tb-btn" onClick={() => navigate(`/activations/${a.id}`)}>Open event</button>
                                    {st.slug && (
                                        <a className="tb-btn" href={`/gallery/${st.slug}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                            Public gallery
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* Schedule: this week + next */}
            {acts !== null && (
                <>
                    <TwoWeekCalendar
                        events={calEvents}
                        start={calStart}
                        onShift={(n) => setCalStart((s) => addDays(s, 7 * n))}
                        onToday={() => setCalStart(mondayOf(Date.now()))}
                        navigate={navigate}
                    />
                    <CalLegend />
                </>
            )}

            {/* Up next */}
            <p className="s-lab" style={{ marginTop: 24 }}>Up next · 7 days</p>
            {acts === null && !failed ? (
                <TCard><TSkeleton rows={3} /></TCard>
            ) : upNext.length === 0 ? (
                <TCard><TEmpty message="Nothing in the next 7 days." actionLabel="See all activations" onAction={() => navigate('/activations')} /></TCard>
            ) : (
                <TCard>
                    {upNext.map((a, i) => {
                        const wx = extra[a.id]?.weather;
                        const label = dateRangeLabel(a.date?.split('T')[0] + 'T12:00:00');
                        return (
                            <TRow
                                key={a.id}
                                index={i}
                                name={a.name}
                                meta={[a.location, wx ? `${wx.temp}° ${wx.label}` : ''].filter(Boolean).join(' · ')}
                                metaDate={label}
                                date={label}
                                status={readiness(a)}
                                href={`/activations/${a.id}`}
                                onOpen={() => navigate(`/activations/${a.id}`)}
                                menuItems={[
                                    { label: 'Open', onClick: () => navigate(`/activations/${a.id}`) },
                                    { label: 'Sharing & Delivery', onClick: () => navigate(`/activations/${a.id}/sharing`) },
                                ]}
                            />
                        );
                    })}
                </TCard>
            )}

            <div className={SANDBOX_DATA ? 'h-split' : undefined}>
                {/* Latest captures */}
                <section>
                    <p className="s-lab" style={{ marginTop: 24 }}>Latest captures</p>
                    {photos === null ? (
                        <TCard><TSkeleton rows={2} /></TCard>
                    ) : photos.length === 0 ? (
                        <TCard><TEmpty message="Captures from live events appear here." /></TCard>
                    ) : (
                        <div className="capgrid">
                            {photos.slice(0, 10).map((p) => {
                                const a = byId.get(p.activationId);
                                return (
                                    <a
                                        key={p.id}
                                        className="cap"
                                        href={`/activations/${p.activationId}/media`}
                                        onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${p.activationId}/media`); } }}
                                    >
                                        <img src={p.thumbnailUri} alt={`Capture from ${a?.name ?? 'event'}`} loading="lazy" />
                                        <span>{a?.name ?? ''} · {ago(p.createdAt)}</span>
                                    </a>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* My tasks (fake data only; no backend yet) */}
                {SANDBOX_DATA && <section>
                    <p className="s-lab" style={{ marginTop: 24 }}>My tasks{tasks ? ` · ${openTasks.length} open` : ''}</p>
                    {tasks === null ? (
                        <TCard><TSkeleton rows={3} /></TCard>
                    ) : openTasks.length === 0 && justDone.length === 0 ? (
                        <TCard><TEmpty message="You’re all caught up." /></TCard>
                    ) : (
                        <TCard>
                            {(tasks ?? [])
                                .filter((t) => t.status !== 'done' || justDone.includes(t.id))
                                .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
                                .map((t) => {
                                    const done = t.status === 'done';
                                    const due = dueLabel(t.dueAt);
                                    return (
                                        <div className={done ? 'task done' : 'task'} key={t.id}>
                                            <label className="check task-ck">
                                                <input
                                                    type="checkbox"
                                                    checked={done}
                                                    aria-label={`Mark “${t.title}” ${done ? 'not done' : 'done'}`}
                                                    onChange={(e) => {
                                                        setTaskDone(t, e.target.checked);
                                                        setJustDone((ids) => (e.target.checked ? [...ids, t.id] : ids.filter((x) => x !== t.id)));
                                                    }}
                                                />
                                                <span className="box" aria-hidden="true">
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m5 12 4 4L19 6" /></svg>
                                                </span>
                                            </label>
                                            <span className="t-main">
                                                <span className="t-title">{t.title}</span>
                                                {t.activationName && (
                                                    <a
                                                        className="t-ev"
                                                        href={`/activations/${t.activationId}`}
                                                        onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${t.activationId}`); } }}
                                                    >
                                                        {t.activationName}
                                                    </a>
                                                )}
                                            </span>
                                            <span className={due === 'Overdue' && !done ? 't-due late' : 't-due'}>
                                                {done ? 'Done' : due}
                                            </span>
                                        </div>
                                    );
                                })}
                        </TCard>
                    )}
                </section>}
            </div>
        </AppShell>
    );
}
